import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import Loader from 'components/shared/loader/loader.component';
import InfoText from 'components/shared/info-text.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { outgoingSupplierRequestsTemplate } from 'apollo/templates/supplier-request';
import { SupplierRequest, SupplierRequestStatus } from 'models/supplier-request';
import SupplierRequestTable from './supplier-request-table.component';

/** Pending requests first, then the history; each group newest first (the server's order). */
const pendingFirst = (requests: SupplierRequest[]) => [
    ...requests.filter((request) => request.status === SupplierRequestStatus.PENDING),
    ...requests.filter((request) => request.status !== SupplierRequestStatus.PENDING),
];

/** DD-3662: this lab's requests to labs it wants as suppliers — waiting or declined. */
const OutgoingSupplierRequests: FC = () => {
    const { t: translate } = useTranslation(['client-directory']);
    const { showBoundary } = useDenthubErrorBoundary();
    // DD-3674 hid everything but pending; DD-3707 shows declined ones too (with the reason).
    // An approved one is in the supplier list already, so it stays hidden. The status
    // argument takes a single status, so fetch all and drop the approved ones here.
    const { data, loading: isLoading } = useQuery(outgoingSupplierRequestsTemplate, {
        fetchPolicy: 'cache-and-network',
        onError: (error) => showBoundary(error),
    });
    const requests: SupplierRequest[] = (data?.outgoingSupplierRequests ?? []).filter(
        (request: SupplierRequest) => request.status !== SupplierRequestStatus.APPROVED,
    );
    if (isLoading && !data) return <Loader />;

    return (
        <Fragment>
            {requests.length ? (
                <InfoText text={translate('client-directory:OUTGOING_SUPPLIER_REQUESTS_INFO')} />
            ) : undefined}
            <Box mt={2}>
                {requests.length ? (
                    <SupplierRequestTable
                        requests={pendingFirst(requests)}
                        labOf={(request) => request.supplier}
                        dataTest="supplier-requests.outgoing"
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {translate('client-directory:NO_SUPPLIER_REQUESTS')}
                    </Typography>
                )}
            </Box>
        </Fragment>
    );
};

export default OutgoingSupplierRequests;
